import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServiceCategoryService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching service category list.
 *
 * @param {Object} params - Query parameters (page, limit, search, status)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListServiceCategory = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["service-category-list", params],
    queryFn: () => {
      return ServiceCategoryService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single service category.
 *
 * @param {string} uuid - Service Category UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetServiceCategory = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["service-category", uuid],
    queryFn: () => {
      return ServiceCategoryService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for adding a new service category.
 *
 * @param {function} onSuccess - Callback to run if the add request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the add request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useAddServiceCategory = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return ServiceCategoryService.add(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["service-category-list"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for updating an existing service category.
 *
 * @param {function} onSuccess - Callback to run if the update request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the update request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateServiceCategory = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return ServiceCategoryService.update(data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["service-category-list"] });
      queryClient.invalidateQueries({ queryKey: ["service-category", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for toggling a service category's active/inactive status.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateServiceCategoryStatus = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, isActive }) => {
      return ServiceCategoryService.updateStatus(uuid, isActive);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["service-category-list"] });
      queryClient.invalidateQueries({ queryKey: ["service-category", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for deleting a service category.
 *
 * @param {function} onSuccess - Callback to run if the delete request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the delete request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useServiceCategoryDelete = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return ServiceCategoryService.delete(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["service-category-list"] });
      queryClient.invalidateQueries({ queryKey: ["service-category", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useListServiceCategory,
  useGetServiceCategory,
  useAddServiceCategory,
  useUpdateServiceCategory,
  useUpdateServiceCategoryStatus,
  useServiceCategoryDelete,
};
